import { App, createApp, defineComponent, Component, VNode } from 'vue';
import PrimeVue from 'primevue/config';
import Ripple from 'primevue/ripple';
import Aura from '@primeuix/themes/aura';

import DialogComponent from './VueDialog.vue';
import { DIALOG_ROOT_ID, DIALOG_TEXT_TYPE, DIALOG_TYPE } from './constants';

type TDialogAppConfiguratorCb = (app: App<Element>) => void;

class Dialog {
  private static appConfiguratorCallbacks: TDialogAppConfiguratorCb | null = null;

  static setAppConfiguratorCallback(cb: TDialogAppConfiguratorCb): void {
    this.appConfiguratorCallbacks = cb;
  }

  private getContainer(): HTMLElement {
    const container = document.getElementById(DIALOG_ROOT_ID);

    if (!container) {
      throw new Error('You are trying to use the `Dialog` before it was initialized. Please call `app.use(DialogPlugin)` first.');
    }

    return container;
  }

  private initApp(AppComponent: Component): App<Element> {
    const vueApp = createApp(AppComponent);
    vueApp.use(PrimeVue, {
      theme: {
        preset: Aura,
        options: {
          darkModeSelector: '.p-dark',
          cssLayer: true,
        },
      },
    });
    vueApp.directive('ripple', Ripple);

    if (Dialog.appConfiguratorCallbacks) {
      Dialog.appConfiguratorCallbacks(vueApp);
    }

    vueApp.mount(this.getContainer());
    document.body.style.overflow = 'hidden';

    return vueApp;
  }

  private destroyApp(vueApp: App<Element>): void {
    vueApp.unmount();
    document.body.style.overflow = '';
  }

  private showDialog(title: string, message: string, type: DIALOG_TYPE, textType?: DIALOG_TEXT_TYPE): Promise<boolean> {
    return new Promise((resolve) => {
      let vueApp: App<Element> | null = null;

      const closeModal = (answer: boolean): void => {
        this.destroyApp(vueApp!);
        resolve(answer);
      };

      const AppComponent = defineComponent({
        setup() {
          return (): VNode => (
            <DialogComponent title={title} message={message} type={type} textType={textType} onClose={closeModal} />
          );
        },
      });

      vueApp = this.initApp(AppComponent);
    });
  }

  Alert(title: string, message: string, textType?: DIALOG_TEXT_TYPE): Promise<void> {
    return this.showDialog(title, message, DIALOG_TYPE.ALERT, textType).then(answer => void answer);
  }

  Confirm(title: string, message: string, textType?: DIALOG_TEXT_TYPE): Promise<boolean> {
    return this.showDialog(title, message, DIALOG_TYPE.CONFIRM, textType);
  }
}

export { Dialog as DialogClass };
export default new Dialog();
